import { useState } from 'react';
import { useAppDispatch } from '@/shared/lib/hooks/useAppDispatch';
import { useAppSelector } from '@/shared/lib/hooks/useAppSelector';
import { useNavigate } from 'react-router-dom';
import {
    passQuestionnaire,
    setInstanceId,
    setRiskResult,
} from '@/features/questionnaire/model/questionnaireSlice';
import {
    submitSurveyAnswers,
    requestRiskCalculation,
    getRiskStatus,
} from '@/features/questionnaire/api/questionnaireApi';
import { requestAndPollTrainingPlan } from '@/features/training/model/trainingSlice';
import { requestAndPollNutritionPlan } from '@/features/nutrition/model/nutritionSlice';

interface SubmitAnswer {
    question_id: number;
    typed_value?: string;
    option_ids?: string[];
}

export const useSubmitSurvey = () => {
    const dispatch = useAppDispatch();
    const surveyId = useAppSelector((state) => state.questionnaire.surveyId);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [progress, setProgress] = useState(0);
    const navigate = useNavigate();

    const submitSurvey = async (answers: Record<number, any>, survey: any) => {
        if (!surveyId || !survey?.blocks) {
            console.error('surveyId или survey не найден');
            return null;
        }

        setIsSubmitting(true);
        setProgress(0);

        const transformedAnswers: SubmitAnswer[] = [];

        for (const [questionIdStr, value] of Object.entries(answers)) {
            const question_id = Number(questionIdStr);
            const question = survey.blocks
                .flatMap((block: any) => block.questions || [])
                .find((q: any) => q.question_id === question_id);
            if (!question) continue;

            if (question.question_type === 'checkbox') {
                const selectedOptionIds = question.options
                    .filter((opt: any) => value.includes(opt.value))
                    .map((opt: any) => String(opt.option_id));
                transformedAnswers.push({ question_id, option_ids: selectedOptionIds });
            } else if (['radio', 'sex'].includes(question.question_type)) {
                const selected = question.options.find((opt: any) => opt.value === value);
                if (selected) {
                    transformedAnswers.push({ question_id, option_ids: [String(selected.option_id)] });
                }
            } else {
                transformedAnswers.push({ question_id, typed_value: String(value) });
            }
        }

        try {
            setProgress(10);

            const { instance_id } = await submitSurveyAnswers(surveyId, transformedAnswers);
            dispatch(setInstanceId(instance_id));
            setProgress(30);

            const { request_id } = await requestRiskCalculation(instance_id);
            setProgress(40);

            // 🧠 Запускаем тренировочный и пищевой планы параллельно
            dispatch(requestAndPollTrainingPlan(instance_id));
            dispatch(requestAndPollNutritionPlan(instance_id));

            // ⏳ Поллинг по рискам (UI зависит от него)
            let pollingProgress = 40;
            const progressInterval = setInterval(() => {
                pollingProgress = Math.min(pollingProgress + 1, 95);
                setProgress(pollingProgress);
            }, 500);

            const poll = async (): Promise<any> => {
                try {
                    const res = await getRiskStatus(request_id);
                    if (res.status === 'completed') {
                        clearInterval(progressInterval);
                        setProgress(100);
                        dispatch(setRiskResult(res));
                        dispatch(passQuestionnaire());
                        setIsSubmitting(false);
                        navigate('/my-health');
                        return res;
                    }

                    if (res.status === 'failed') {
                        clearInterval(progressInterval);
                        setIsSubmitting(false);
                        console.error('❌ Не удалось рассчитать риски');
                        return null;
                    }
                } catch (err: any) {
                    if (err?.response?.status !== 422) {
                        clearInterval(progressInterval);
                        setIsSubmitting(false);
                        throw err;
                    }
                }

                await new Promise((resolve) => setTimeout(resolve, 1000));
                return poll();
            };

            return await poll();
        } catch (error) {
            console.error('Ошибка полной отправки:', error);
            setIsSubmitting(false);
            return null;
        }
    };

    return { submitSurvey, isSubmitting, progress };
};
